import { useCallback, useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase B4 — onboarding state hook.
 *
 * Single source of truth for `tour_status`, `current_step_id`, completed
 * step ids, the live go-live checklist, and the widget-visibility flag.
 *
 * - `refresh()` re-fetches /api/onboarding/me (used after route changes
 *   that may have ticked an item — e.g. a site is created and the
 *   checklist row should flip from "Create your first site" pending → done).
 * - `patch({...})` PATCHes /api/onboarding/me and merges the response
 *   back into local state synchronously.
 * - `reset()` resets the tour to `not_started` and re-fetches.
 * - `seedSample()` calls the sample-data endpoint and toasts the counts.
 */
export default function useOnboarding(authedUser) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const inflightRef = useRef(false);

  const refresh = useCallback(async () => {
    if (!authedUser) { setData(null); setLoading(false); return; }
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const { data: payload } = await api.get("/onboarding/me");
      setData(payload);
    } catch {
      // Silent — endpoint should always succeed once the user is auth'd.
      // Toasting here would noise up every page load with no actionable copy.
    } finally {
      inflightRef.current = false;
      setLoading(false);
    }
  }, [authedUser]);

  useEffect(() => { refresh(); }, [refresh]);

  const patch = useCallback(async (changes) => {
    try {
      const { data: newTour } = await api.patch("/onboarding/me", changes);
      setData((prev) => prev ? { ...prev, tour: newTour } : prev);
      return newTour;
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  }, []);

  const reset = useCallback(async () => {
    try {
      const { data: newTour } = await api.post("/onboarding/me/reset");
      setData((prev) => prev ? { ...prev, tour: newTour } : prev);
      // Re-fetch the checklist alongside the tour state — both come from
      // /onboarding/me so a follow-up GET keeps them in sync.
      await refresh();
      return newTour;
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  }, [refresh]);

  const seedSample = useCallback(async () => {
    try {
      const { data: result } = await api.post("/onboarding/seed-sample-data");
      toast.success(
        `Sample data added — ${result.counts.sites} site, ${result.counts.hosts} hosts, ${result.counts.pre_registrations} pre-reg.`,
      );
      await refresh();
      return result;
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  }, [refresh]);

  return { data, loading, refresh, patch, reset, seedSample };
}
